import React, { useState, useRef } from 'react';
import { useTheme } from './ThemeContext';
import { useNavigate } from 'react-router-dom'; // Added for navigation
import { 
  TrendingUp, Globe, ArrowRight, Sparkles, Database, 
  Rocket, ShieldCheck, Microscope, HeartHandshake, Pause 
} from 'lucide-react';

import { Swiper, SwiperSlide } from 'swiper/react';
import { Autoplay, Pagination, Navigation } from 'swiper/modules';
import 'swiper/css';
import 'swiper/css/pagination';

function Platform({ isVisible = true }) {
  const { isDarkMode } = useTheme();
  const navigate = useNavigate(); // Hook for navigation
  const swiperRef = useRef(null);
  const [isPaused, setIsPaused] = useState(false);

  const features = [
    {
      id: 'precision-matching',
      title: 'Precision Matching',
      desc: 'We find the environment where you will thrive based on your personality and career goals.',
      icon: <Microscope className="w-8 h-8" />,
      gradient: 'from-blue-600 to-indigo-500'
    },
    {
      id: 'smart-analytics',
      title: 'Smart Analytics',
      desc: 'Gain clarity on your chances with real data on historical acceptance rates and trends.',
      icon: <TrendingUp className="w-8 h-8" />,
      gradient: 'from-purple-600 to-fuchsia-500'
    },
    {
      id: 'unified-dashboard',
      title: 'Unified Dashboard',
      desc: 'Keep documents, deadlines, and communications in one clean, centralized space.',
      icon: <Database className="w-8 h-8" />,
      gradient: 'from-orange-500 to-red-600'
    },
    {
      id: 'verified-network',
      title: 'Verified Network',
      desc: 'Direct partnerships with institutions globally to ensure your application gets noticed.',
      icon: <ShieldCheck className="w-8 h-8" />,
      gradient: 'from-emerald-500 to-teal-600'
    },
    {
      id: 'human-support',
      title: 'Human Support',
      desc: 'Expert counselors who understand the stress of moving abroad and guide you personally.',
      icon: <HeartHandshake className="w-8 h-8" />,
      gradient: 'from-pink-500 to-rose-600'
    },
    {
      id: 'global-gateway',
      title: 'Global Gateway',
      desc: 'Explore opportunities in 30+ countries with local complexity handled by our experts.',
      icon: <Globe className="w-8 h-8" />,
      gradient: 'from-blue-400 to-cyan-500'
    }
  ];

  const handleMouseEnter = () => {
    setIsPaused(true);
    if (swiperRef.current) {
      swiperRef.current.autoplay.stop();
      swiperRef.current.wrapperEl.style.transitionDuration = '0ms';
    }
  };

  const handleMouseLeave = () => {
    setIsPaused(false);
    if (swiperRef.current) {
      swiperRef.current.autoplay.start();
      swiperRef.current.wrapperEl.style.transitionDuration = '8000ms';
    }
  };

  // Navigation Handler
  const handleFeatureClick = (id) => {
    navigate(`/platform/${id}`);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <section className={`relative pt-4 pb-20 px-4 overflow-hidden transition-colors duration-500 ${
      isDarkMode ? 'bg-[#030712]' : 'bg-gray-50'
    }`}>
      {/* Dynamic Background Pattern */}
      <div className={`absolute inset-0 ${isDarkMode ? 'opacity-10' : 'opacity-5'}`}>
        <div className="absolute inset-0" style={{
            backgroundImage: `linear-gradient(${isDarkMode ? 'rgba(59,130,246,0.1)' : 'rgba(0,0,0,0.1)'} 1px, transparent 1px),
                             linear-gradient(90deg, ${isDarkMode ? 'rgba(59,130,246,0.1)' : 'rgba(0,0,0,0.1)'} 1px, transparent 1px)`,
            backgroundSize: '40px 40px'
          }}
        />
      </div>

      <div className="max-w-[1400px] mx-auto relative z-10">
        
        {/* HEADER SECTION */}
        <div className={`text-center mb-12 transition-all duration-1000 ${
          isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
        }`}>
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-blue-500/10 border border-blue-500/20 mb-4 shadow-sm">
            <Sparkles className="w-3.5 h-3.5 text-blue-500" />
            <span className="text-[10px] font-black tracking-[0.2em] text-blue-500 uppercase">Our Capabilities</span>
          </div>

          <h2 className={`text-3xl sm:text-5xl font-black mb-4 tracking-tight leading-tight ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
            Built for the <span className="bg-gradient-to-r from-blue-500 via-purple-500 to-pink-500 text-transparent bg-clip-text">Modern Student</span>
          </h2>

          <p className={`text-base max-w-2xl mx-auto font-medium ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
            Vidyazon simplifies your global education path with smart technology and expert human counseling.
          </p>
        </div>

        {/* INTERACTIVE CAROUSEL */}
        <div className="relative" onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave}>
          {isPaused && (
            <div className="absolute -top-6 left-1/2 -translate-x-1/2 z-50 flex items-center gap-2 px-3 py-1 rounded-full bg-blue-600 text-white text-[9px] font-black uppercase tracking-widest shadow-xl animate-bounce">
              <Pause size={10} fill="currentColor" /> Click to Explore
            </div>
          )}

          <Swiper
            onSwiper={(swiper) => (swiperRef.current = swiper)}
            modules={[Autoplay, Pagination, Navigation]}
            slidesPerView={1}
            spaceBetween={20}
            loop={true}
            speed={8000}
            autoplay={{ delay: 0, disableOnInteraction: false }}
            breakpoints={{
              640: { slidesPerView: 2 },
              1024: { slidesPerView: 3 },
              1440: { slidesPerView: 4 }
            }}
            className="platform-swiper pb-10"
          >
            {features.map((feature, i) => (
              <SwiperSlide key={i} className="h-full">
                <div 
                  onClick={() => handleFeatureClick(feature.id)}
                  className={`group relative h-[340px] rounded-[2rem] p-8 border transition-all duration-500 flex flex-col cursor-pointer
                  ${isDarkMode ? 'bg-gray-900/60 border-gray-800 hover:border-blue-500/30' : 'bg-white border-gray-100 shadow-lg hover:shadow-2xl hover:-translate-y-1'}`}
                >
                  <div className={`w-14 h-14 rounded-xl flex items-center justify-center text-white mb-6 
                    bg-gradient-to-br ${feature.gradient} shadow-lg transition-transform group-hover:rotate-3 group-hover:scale-105`}>
                    {feature.icon}
                  </div>
                  <h3 className={`text-xl font-bold mb-3 tracking-tight ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                    {feature.title}
                  </h3>
                  <p className={`text-sm leading-relaxed mb-4 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                    {feature.desc}
                  </p>
                  <div className="mt-auto flex items-center gap-2 text-xs font-black text-blue-500 opacity-0 group-hover:opacity-100 transition-opacity">
                    View Details <ArrowRight size={14} />
                  </div>
                </div>
              </SwiperSlide>
            ))}
          </Swiper>
        </div>

        {/* COMPACT CTA BANNER */}
        <div className={`mt-10 rounded-3xl p-6 sm:p-10 relative overflow-hidden transition-all duration-700 shadow-2xl ${
            isDarkMode ? 'bg-indigo-950/40 border border-indigo-500/20' : 'bg-indigo-600 text-white'
          }`}>
          <div className="absolute top-0 right-0 w-64 h-64 bg-white/10 rounded-full blur-[80px] -translate-y-1/2 translate-x-1/2" />
          
          <div className="relative z-10 flex flex-col md:flex-row items-center justify-between gap-6">
            <div className="flex items-start gap-4 max-w-xl">
              <div className="p-3 bg-white/20 rounded-2xl hidden sm:block">
                <Rocket className="w-6 h-6 text-white animate-pulse" />
              </div>
              <div>
                <h3 className="text-xl sm:text-2xl font-black mb-2 text-white">Let’s navigate your future together.</h3>
                <p className="text-sm text-white/80 font-medium">
                  Whether you're a student seeking guidance or a counselor looking for better tools, our ecosystem helps you succeed.
                </p>
              </div>
            </div>

            <div className="shrink-0 w-full md:w-auto text-center">
              <a href="/signup" className="inline-flex items-center justify-center gap-2 bg-white text-indigo-700 
                px-8 py-3.5 rounded-2xl text-sm font-black shadow-xl hover:bg-indigo-50 transition-all hover:-translate-y-1 w-full md:w-auto">
                Join the Network <ArrowRight size={16} />
              </a>
            </div>
          </div>
        </div>
      </div>

      <style jsx global>{`
        .platform-swiper .swiper-wrapper { transition-timing-function: linear !important; }
        .platform-swiper .swiper-pagination-bullet { opacity: 0.2; background: #3b82f6 !important; }
        .platform-swiper .swiper-pagination-bullet-active { width: 20px !important; border-radius: 4px !important; opacity: 1; }
      `}</style>
    </section>
  );
}

export default Platform;